import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/common/SEOMetadata';
import Hero from '@/components/Hero';
import TourList from '@/components/tours/TourList';
import { tours, faqs } from '@/data/tourData';
import TourSearch from '@/components/tours/TourSearch';
import TourQuickInfo from '@/components/tours/TourQuickInfo';
import TourPageHeader from '@/components/tours/TourPageHeader';
import TourNote from '@/components/tours/TourNote';
import TourContent from '@/components/tours/TourContent';
import EventLocalBusinessSchema from '@/components/schema/EventLocalBusinessSchema';

// Define a type for tour objects
interface Tour {
  id: number;
  title: string;
  description: string;
  image: string;
  bookingUrl: string;
  featured?: boolean;
  itemId: number;
  teaser?: string;
}

const Tours = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTours, setFilteredTours] = useState<Tour[]>(tours);

  useEffect(() => {
    // Defer FareHarbor script loading for better initial load performance
    const loadFareHarborScript = () => {
      if (typeof window !== 'undefined') {
        const script = document.createElement('script');
        script.src = 'https://fareharbor.com/embeds/api/v1/?autolightframe=yes';
        script.async = true;
        document.body.appendChild(script);
        
        return () => {
          if (document.body.contains(script)) {
            document.body.removeChild(script);
          }
        };
      }
    };
    
    // Use requestIdleCallback or setTimeout to defer non-critical script loading
    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        const handle = window.requestIdleCallback(loadFareHarborScript, { timeout: 2000 });
        return () => window.cancelIdleCallback(handle);
      } else {
        const timeoutId = setTimeout(loadFareHarborScript, 1000);
        return () => clearTimeout(timeoutId);
      }
    }
  }, []);

  useEffect(() => {
    if (searchQuery.length > 0) {
      const filtered = tours.filter(tour => 
        tour.title.toLowerCase().includes(searchQuery) || 
        tour.description.toLowerCase().includes(searchQuery) || 
        tour.teaser?.toLowerCase().includes(searchQuery)
      );
      setFilteredTours(filtered);
    } else {
      setFilteredTours(tours);
    }
  }, [searchQuery]);

  const resetSearch = () => {
    setSearchQuery('');
  };

  // Update the tours with teasers for all tours
  const toursWithTeasers = filteredTours.map(tour => {
    if (tour.title.includes("Brooklyn Heights")) {
      return {...tour, teaser: "You'll hear how Ned the cat crossed the Brooklyn Bridge before humans ever traversed it..."};
    } else if (tour.title.includes("Financial District")) {
      return {...tour, teaser: "Discover the cat that saved Wall Street and the feline who christened NYC's pneumatic mail tube system..."};
    } else if (tour.title.includes("Lower East Side")) {
      return {...tour, teaser: "Learn about the cat mascots of legendary dive bars and the feline who befriended prisoners on Murderers' Row..."};
    } else if (tour.title.includes("Private Tours")) {
      return {...tour, teaser: "Create your own custom cat adventure with stories tailored to your group's interests and pace..."};
    }
    return tour;
  });

  // Adding the new FAQ about seeing real cats on the tour
  const enhancedFaqs = [
    ...faqs,
    {
      question: "Will we see real cats on the tour?",
      answer: "While our tours are primarily about historical cats who've long since used up their nine lives, we do keep our eyes peeled for modern-day feline celebrities! Sometimes they grace us with their presence in windows or on stoops, but like all cats, they operate on their own mysterious schedule. We make no guarantees about cat sightings—they're notoriously bad at keeping appointments!"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <SEOMetadata
        title="Tours"
        description="Browse all available cat history walking tours in NYC. Discover the stories of the city's most fascinating feline residents."
        canonicalUrl="https://catsabouttowntours.com/tours"
        ogImage="/lovable-uploads/93adf40c-ae90-4c69-8b73-4748c9db8a0e.png"
        ogImageAlt="Cat history walking tour"
        keywords={[
          'NYC cat walking tours',
          'Brooklyn Heights cats',
          'feline history tour',
          'cat history New York',
          'Peggy Gavan tour'
        ]}
      />
      <Helmet>
        {/* Breadcrumb structured data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://catsabouttowntours.com/" },
              { "@type": "ListItem", "position": 2, "name": "Tours", "item": "https://catsabouttowntours.com/tours" }
            ]
          })}
        </script>
      </Helmet>
      <EventLocalBusinessSchema
        event={{
          name: 'Cats About Town Walking Tours',
          description:
            "Browse and book historical cat walking tours in New York City.",
          url: 'https://catsabouttowntours.com/tours',
          image: '/lovable-uploads/93adf40c-ae90-4c69-8b73-4748c9db8a0e.png',
          startDate: new Date().toISOString(),
          endDate: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
          performer: 'Peggy Gavan',
          priceRange: '$40 - $320',
          eventStatus: 'https://schema.org/EventScheduled'
        }}
      />
      
      <Hero 
        title="Our Historical Cat Tours" 
        subtitle="Experience New York's history through the stories of its most fascinating feline residents"
        backgroundImage="/lovable-uploads/93adf40c-ae90-4c69-8b73-4748c9db8a0e.png"
        height="medium"
        imageStyle="natural"
        backgroundAlt="Cats About Town Tours overview"
        showUrgencyMessage={true}
      />
      
      {/* Quick Info Bar */}
      <TourQuickInfo />
      
      <section className="py-16 bg-[#FDF6ED]" aria-labelledby="tours-heading">
        <div className="container mx-auto px-4">
          <TourPageHeader 
            title="Explore NYC's Feline History"
            subtitle="Each tour focuses on a specific neighborhood with its own unique collection of 
              historical cat stories. Our expert guides bring these stories to life while showing you 
              the landmarks that shaped New York's history."
          />
          
          {/* Tour note about cat spotting */}
          <TourNote content="Our tours focus on historical stories and not cat-spotting safaris (though if we see a feline friend along the way, we'll consider it a purr-fect bonus!)" />
          
          {/* Search tours */}
          <TourSearch 
            searchQuery={searchQuery} 
            setSearchQuery={setSearchQuery} 
            filteredToursCount={filteredTours.length} 
            totalToursCount={tours.length}
            resetSearch={resetSearch}
          />
          
          {/* Tour list with enhanced teasers */}
          <div aria-live="polite">
            {filteredTours.length > 0 && (
              <TourList tours={toursWithTeasers} />
            )}
          </div>
        </div>
      </section>
      
      <TourContent faqs={enhancedFaqs} />
    </div>
  );
};

export default Tours;
